import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { navigate } from "../../lib/router";
import type { ProjectDetail } from "@shared/schema";

export default function AnalysisView({ project }: { project: ProjectDetail }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["project", project.id] });
  const [urlVal, setUrlVal] = useState("");

  const ingest = useMutation({
    mutationFn: () =>
      api.ingest(project.id, {
        type: project.refSourceType ?? "url",
        value: project.refSourceInput ?? "",
      }),
    onSuccess: refresh,
  });
  const uploadMut = useMutation({
    mutationFn: (file: File) => api.uploadReference(project.id, file),
    onSuccess: refresh,
  });
  const urlMut = useMutation({
    mutationFn: () => api.ingest(project.id, { type: "url", value: urlVal.trim() }),
    onSuccess: refresh,
  });
  const analyze = useMutation({ mutationFn: () => api.analyze(project.id), onSuccess: refresh });
  const plan = useMutation({
    mutationFn: () => api.plan(project.id),
    onSuccess: async () => {
      await refresh();
      navigate(`/projects/${project.id}/board`);
    },
  });

  const a = project.analysis;
  const analyzing = project.status === "analyzing";
  const planning = project.status === "planning";
  const busy =
    ingest.isPending || analyze.isPending || plan.isPending || uploadMut.isPending || urlMut.isPending || analyzing || planning;
  const err = ingest.error || analyze.error || plan.error || uploadMut.error || urlMut.error;
  const hasVideo = !!project.refVideoR2Key;

  return (
    <div className="grid" style={{ gridTemplateColumns: "1fr", gap: 16 }}>
      <div className="card pad">
        <h2>Референс</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", justifyContent: "space-between" }}>
          <div style={{ minWidth: 200, flex: "1 1 320px" }}>
            <div className="hint">Джерело</div>
            <div style={{ fontWeight: 600, wordBreak: "break-all" }}>
              {project.refSourceType ?? "—"}: <span style={{ color: "var(--muted)" }}>{project.refSourceInput ?? "—"}</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button className="btn sm" disabled={busy || !project.refSourceInput || project.refSourceType === "upload"} onClick={() => ingest.mutate()} title="Для Foreplay/URL">
              {ingest.isPending ? <span className="spinner" /> : "1 · Підтягнути відео"}
            </button>
            <button className="btn sm" disabled={busy || !hasVideo} onClick={() => analyze.mutate()}>
              {analyzing || analyze.isPending ? <><span className="spinner" /> Аналізуємо…</> : "2 · Аналіз (Gemini)"}
            </button>
            <button className="btn primary sm" disabled={busy || !a} onClick={() => plan.mutate()}>
              {planning || plan.isPending ? <><span className="spinner" /> Розбиваємо…</> : "3 · Розбити на сцени"}
            </button>
          </div>
        </div>

        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--line-2)" }}>
          <div className="hint" style={{ marginBottom: 6 }}>
            {hasVideo ? "✅ Відео в сховищі — тисни «2 · Аналіз»." : "Завантаж MP4 або встав пряме відео-URL:"}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <label className="btn sm" style={{ cursor: "pointer" }}>
              {uploadMut.isPending ? <span className="spinner" /> : "⤒ Завантажити MP4"}
              <input
                type="file"
                accept="video/mp4,video/quicktime,video/*"
                style={{ display: "none" }}
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadMut.mutate(f);
                }}
              />
            </label>
            <span className="hint">або</span>
            <input
              className="input"
              style={{ flex: "1 1 260px" }}
              placeholder="https://…/video.mp4"
              value={urlVal}
              onChange={(e) => setUrlVal(e.target.value)}
            />
            <button className="btn sm" disabled={busy || !urlVal.trim()} onClick={() => urlMut.mutate()}>
              {urlMut.isPending ? <span className="spinner" /> : "Підтягнути з URL"}
            </button>
          </div>
        </div>
        {err && <div className="hint" style={{ color: "var(--red)", marginTop: 8 }}>{(err as Error).message}</div>}
      </div>

      {!a ? (
        <div className="empty">
          <div className="big">🔎</div>
          <h2>Аналіз ще не зроблено</h2>
          <div>Підтягни відео й запусти аналіз — побачиш покадровий розбір, текст на екранах і бренд-елементи.</div>
        </div>
      ) : (
        <>
          <div className="card pad">
            <h2>Технічні параметри</h2>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[
                a.tech.durationSec ? `${a.tech.durationSec}s` : null,
                a.tech.width && a.tech.height ? `${a.tech.width}×${a.tech.height}` : null,
                a.tech.aspect,
                a.tech.fps ? `${a.tech.fps}fps` : null,
                a.tech.format,
                a.tech.shootingStyle,
              ]
                .filter(Boolean)
                .map((c, i) => (
                  <span key={i} className="chip">{c}</span>
                ))}
            </div>
            {a.narrativeSummary && <div className="hint" style={{ marginTop: 10 }}>{a.narrativeSummary}</div>}
          </div>

          <div className="card pad">
            <h2>Покадровий розбір ({a.shots.length})</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {a.shots.map((s, i) => {
                const isApp = a.appScreens.some((sc) => sc.timecode === s.timecode);
                return (
                  <div key={i} style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--line-2)" }}>
                    <span className="badge blue" style={{ alignSelf: "flex-start" }}>{s.timecode}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{s.action}</div>
                      <div className="hint">
                        {[s.setting, s.lighting, s.cameraAngle].filter(Boolean).join(" · ")}
                      </div>
                      {s.onScreenText && s.onScreenText.length > 0 && (
                        <div className="hint">📝 {s.onScreenText.map((t) => `"${t.text}"`).join(", ")}</div>
                      )}
                    </div>
                    {isApp && <span className="badge amber">App-UI</span>}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card pad">
            <h2>Бренд-елементи референсу</h2>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              {a.brand.palette.map((c, i) => (
                <span key={i} className="chip">
                  <span style={{ width: 14, height: 14, borderRadius: 4, background: c.hex, display: "inline-block", border: "1px solid var(--line)" }} />
                  {c.hex}
                </span>
              ))}
            </div>
            {a.brand.fonts.length > 0 && <div className="hint">Шрифти: {a.brand.fonts.join(", ")}</div>}
            {a.brand.logoDesc && <div className="hint">Лого: {a.brand.logoDesc}</div>}
          </div>
        </>
      )}
    </div>
  );
}
